/**
 * Environment Variables Validation & Configuration
 * 
 * React + Vite 환경에서 안전한 환경 변수 검증 및 관리
 * - 타입 안전성 보장
 * - 필수 변수 누락 검증
 * - 프로덕션/개발 환경 구분
 * - 런타임 검증 및 에러 처리
 */

// Environment validation schema
interface EnvironmentVariables {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_TOSSPAYMENTS_CLIENT_KEY?: string;
  NODE_ENV: 'development' | 'production' | 'test';
  MODE: string;
  DEV: boolean;
  PROD: boolean;
}

// URL validation regex pattern
const URL_PATTERN = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

// Supabase URL pattern for additional validation
const SUPABASE_URL_PATTERN = /^https:\/\/[a-z0-9-]+\.supabase\.co$/;

/**
 * Environment variable validation errors
 */
export class EnvironmentError extends Error {
  constructor(message: string, public readonly variable?: string) {
    super(message);
    this.name = 'EnvironmentError';
  }
}

/**
 * Validates a URL format
 */
function validateUrl(url: string, name: string): string {
  if (!URL_PATTERN.test(url)) {
    throw new EnvironmentError(`Invalid URL format for ${name}: ${url}`, name);
  }
  return url;
}

/**
 * Validates Supabase URL format specifically
 */
function validateSupabaseUrl(url: string): string {
  validateUrl(url, 'VITE_SUPABASE_URL');
  
  if (!SUPABASE_URL_PATTERN.test(url)) {
    throw new EnvironmentError(
      `Invalid Supabase URL format. Expected format: https://your-project.supabase.co, got: ${url}`,
      'VITE_SUPABASE_URL'
    );
  }
  
  return url;
}

/**
 * Validates Supabase anonymous key format
 */
function validateSupabaseAnonKey(key: string): string {
  if (!key || key.length < 50) {
    throw new EnvironmentError(
      'Invalid Supabase anonymous key. Key must be at least 50 characters long.',
      'VITE_SUPABASE_ANON_KEY'
    );
  }
  
  // Basic JWT format check (should start with 'ey')
  if (!key.startsWith('ey')) {
    throw new EnvironmentError(
      'Invalid Supabase anonymous key format. Key should be a valid JWT token.',
      'VITE_SUPABASE_ANON_KEY'
    );
  }
  
  return key;
}

/**
 * Gets and validates environment variables
 */
function getEnvironmentVariables(): EnvironmentVariables {
  // Get raw environment variables
  const rawEnv = {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL as string,
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    VITE_TOSSPAYMENTS_CLIENT_KEY: import.meta.env.VITE_TOSSPAYMENTS_CLIENT_KEY as string,
    NODE_ENV: import.meta.env.NODE_ENV as 'development' | 'production' | 'test',
    MODE: import.meta.env.MODE as string,
    DEV: import.meta.env.DEV as boolean,
    PROD: import.meta.env.PROD as boolean,
  };

  // Validate required variables
  const requiredVars = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'] as const;
  
  for (const varName of requiredVars) {
    if (!rawEnv[varName]) {
      throw new EnvironmentError(
        `Missing required environment variable: ${varName}. Please check your .env file.`,
        varName
      );
    }
  }

  // Validate and transform variables
  const env: EnvironmentVariables = {
    VITE_SUPABASE_URL: validateSupabaseUrl(rawEnv.VITE_SUPABASE_URL),
    VITE_SUPABASE_ANON_KEY: validateSupabaseAnonKey(rawEnv.VITE_SUPABASE_ANON_KEY),
    VITE_TOSSPAYMENTS_CLIENT_KEY: rawEnv.VITE_TOSSPAYMENTS_CLIENT_KEY,
    NODE_ENV: rawEnv.NODE_ENV || 'development',
    MODE: rawEnv.MODE || 'development',
    DEV: rawEnv.DEV ?? true,
    PROD: rawEnv.PROD ?? false,
  };

  return env;
}

/**
 * Cached environment variables (validated once)
 */
let cachedEnv: EnvironmentVariables | null = null;

/**
 * Get validated environment variables (cached)
 */
export function getEnv(): EnvironmentVariables {
  if (!cachedEnv) {
    try {
      cachedEnv = getEnvironmentVariables();
      
      // Development mode logging
      if (cachedEnv.DEV) {
        console.log('✅ Environment variables validated successfully');
        console.log(`🌍 Mode: ${cachedEnv.MODE}`);
        console.log(`🔗 Supabase URL: ${cachedEnv.VITE_SUPABASE_URL}`);
      }
    } catch (error) {
      console.error('❌ Environment validation failed:', error);
      throw error;
    }
  }
  
  return cachedEnv;
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return getEnv().NODE_ENV === 'development';
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  return getEnv().NODE_ENV === 'production';
}

/**
 * Check if running in test mode
 */
export function isTest(): boolean {
  return getEnv().NODE_ENV === 'test';
}

/**
 * Get Supabase configuration
 */
export function getSupabaseConfig() {
  const env = getEnv();
  
  return {
    url: env.VITE_SUPABASE_URL,
    anonKey: env.VITE_SUPABASE_ANON_KEY,
  } as const;
}

/**
 * Get TossPayments configuration (optional)
 */
export function getTossPaymentsConfig() {
  const env = getEnv();
  
  if (!env.VITE_TOSSPAYMENTS_CLIENT_KEY) {
    console.warn('⚠️ VITE_TOSSPAYMENTS_CLIENT_KEY not configured');
    return null;
  }
  
  return {
    clientKey: env.VITE_TOSSPAYMENTS_CLIENT_KEY,
  } as const;
}

/**
 * Validate environment on module load in development
 */
if (typeof window !== 'undefined' && isDevelopment()) {
  try {
    getEnv();
  } catch (error) {
    console.error('🚨 Environment validation failed on startup:', error);
  }
}

// Export validated environment for direct access
export const env = getEnv();